/**
 * useArcEditorCanvasState.ts
 *
 * Owns the arc canvas's edit state (EAINT-951 Phase 3): converts the loaded
 * `ArcTemplateDefinition` into an `ArcCanvasModel` (`arcCanvasModel.ts`), tracks which stage is
 * selected, and exposes editing operations — `renameStage`, `addStage` — as callbacks a render
 * prop (e.g. `ArcLaneGridMolecule`'s `renderStageHeader` -> `ArcStageCardMolecule`'s `onRename`,
 * or `BlockPaletteMolecule`'s `onAddStage`) can call directly. `dirty` flips true the first time
 * an edit actually changes the model (`renameStageInModel` returns the same reference on a
 * no-op rename — an unknown stage id — so a no-op never marks the draft dirty; `addStage` has no
 * no-op case, since a new stage always changes the model).
 *
 * Deliberately narrow: this is the first slice of the page-wiring hook the full canvas needs
 * (`ArcEditorCanvasOrganism.stories.tsx` shows the full render-prop surface it must eventually
 * cover — palette, lane-grid cells, flow view, the four selection kinds, footer). Those grow
 * onto this hook one render prop at a time; see EAINT-951 IMPLEMENTATION_PLAN.md Phase 3.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ArcTemplateDefinition, AspectRequirement } from 'types/arc';
import {
  addAction as addActionToModel,
  addIntent as addIntentToModel,
  addKeyDate as addKeyDateToModel,
  addStage as addStageToModel,
  addStageRequirement as addStageRequirementToModel,
  addTransitionTarget as addTransitionTargetToModel,
  applyStarter as applyStarterToModel,
  definitionToModel,
  firstExitActionIdOf,
  removeAction as removeActionFromModel,
  removeIntent as removeIntentFromModel,
  removeKeyDate as removeKeyDateFromModel,
  removeStage as removeStageFromModel,
  removeStageRequirement as removeStageRequirementFromModel,
  removeTransition as removeTransitionFromModel,
  renameStage as renameStageInModel,
  summaryCountsFor,
  updateAction as updateActionInModel,
  updateArcMeta as updateArcMetaInModel,
  updateArcSettings as updateArcSettingsInModel,
  updateIntent as updateIntentInModel,
  updateKeyDate as updateKeyDateInModel,
  updateStage as updateStageInModel,
  updateStageRequirement as updateStageRequirementInModel,
  updateTransition as updateTransitionInModel,
  type ArcActionUpdates,
  type ArcCanvasDroppableActionType,
  type ArcCanvasModel,
  type ArcCanvasSummaryCounts,
  type ArcIntentUpdates,
  type ArcKeyDateUpdates,
  type ArcMetaUpdates,
  type ArcSettingsUpdates,
  type ArcStageUpdates,
  type ArcTransitionUpdates,
} from '../utils/arc/arcCanvasModel';

/** Identifies the intent selected in the inspector rail — the intent lives inside its stage's
 * `intents` array, so the selection carries both ids (`removeIntent`/`updateIntent` need the
 * stage to find it). */
export interface ArcCanvasIntentSelection {
  stageId: string;
  intentId: string;
}

/** A stage's exit transition at one of its `AvailableAction`s — the clicked flow edge's
 * (`ArcEdge.actionId`) or, from the stage card's transition button, the stage's first exit
 * (`firstExitActionIdOf`); `actionId: null` is a calculated exit or a stage with no exit yet. */
export interface ArcCanvasTransitionSelection {
  stageId: string;
  actionId: string | null;
}

/** Identifies the action selected in the inspector rail — an action lives inside its stage's
 * `onEnter`/`onExit` array, so the selection carries both ids (`removeAction`/`updateAction` need
 * the stage to find it; which list it's in is resolved from the model, not carried here). */
export interface ArcCanvasActionSelection {
  stageId: string;
  actionId: string;
}

const ZERO_SUMMARY_COUNTS: ArcCanvasSummaryCounts = {
  pathsCount: 0,
  intentsCount: 0,
  actionsCount: 0,
  unreachableCount: 0,
};

export interface UseArcEditorCanvasStateResult {
  /** `null` while there is nothing loaded yet (e.g. the row hasn't resolved). */
  model: ArcCanvasModel | null;
  /** True once an edit has actually changed the model since it was loaded. */
  dirty: boolean;
  /** How many edits have changed the model since it was loaded, saved or discarded — the
   * header's "N unsaved" pill. Counts committed model changes (one per edit that produced a
   * new model), never no-ops; 0 whenever `dirty` is false. */
  unsavedChangeCount: number;
  /** The arc-level inspector (D24) — mutually exclusive with every other selection kind. */
  selectedArc: boolean;
  /** Opens the arc-level inspector; clears any stage/transition/intent/action selection. */
  selectArc: () => void;
  /** Applies `updates` to the arc's own name/description — not selection-guarded (there is
   * exactly one arc per model). */
  updateArcMeta: (updates: ArcMetaUpdates) => void;
  /** Applies `updates` to `arcSettings` (reopenable/allowManualCancellation/autoComplete). */
  updateArcSettings: (updates: ArcSettingsUpdates) => void;
  /** Appends a blank key date row (always marks the draft dirty). */
  addKeyDate: () => void;
  /** Applies `updates` to the key date named `name` (rename/expression/fallback days). */
  updateKeyDate: (name: string, updates: ArcKeyDateUpdates) => void;
  /** Removes the key date named `name`. */
  removeKeyDate: (name: string) => void;
  selectedStageId: string | null;
  /** Selecting a stage clears any selected intent (mutually exclusive selection kinds). */
  selectStage: (stageId: string | null) => void;
  selectedTransition: ArcCanvasTransitionSelection | null;
  /** Selecting a transition clears every other selection kind (spec § Model mapping: selecting
   * an edge selects the source stage's transition). Without an `actionId` the selection resolves
   * to the stage's first exit, so the inspector, the flow view's highlighted edge and Remove all
   * address the same `AvailableAction`. */
  selectTransition: (stageId: string, actionId?: string | null) => void;
  /** Applies `updates` to the currently selected transition; a no-op with nothing selected. */
  updateTransition: (updates: ArcTransitionUpdates) => void;
  /** Removes the currently selected exit (one action of several, or the whole transition) and
   * clears the selection; a no-op with nothing selected. */
  removeTransition: () => void;
  selectedIntent: ArcCanvasIntentSelection | null;
  /** Selecting an intent clears the selected stage and the selected action. */
  selectIntent: (stageId: string, intentId: string) => void;
  /** Applies `updates` to the currently selected intent; a no-op with nothing selected. */
  updateIntent: (updates: ArcIntentUpdates) => void;
  /** Removes the currently selected intent and clears the selection; a no-op with nothing
   * selected. */
  removeIntent: () => void;
  selectedAction: ArcCanvasActionSelection | null;
  /** Selecting an action clears the selected stage and the selected intent. */
  selectAction: (stageId: string, actionId: string) => void;
  /** Applies `updates` to the currently selected action; a no-op with nothing selected. */
  updateAction: (updates: ArcActionUpdates) => void;
  /** Removes the currently selected action and clears the selection; a no-op with nothing
   * selected. */
  removeAction: () => void;
  /** No-ops (and leaves `dirty` unchanged) when `stageId` doesn't match a stage. */
  renameStage: (stageId: string, fallbackName: string) => void;
  /** Applies `updates` to the currently selected stage; a no-op with nothing selected. */
  updateStage: (updates: ArcStageUpdates) => void;
  /** Removes the currently selected stage and clears the selection; a no-op with nothing
   * selected. */
  removeStage: () => void;
  /** Appends a blank requirement to the currently selected stage; a no-op with nothing
   * selected. */
  addStageRequirement: () => void;
  /** Applies `updates` to the requirement at `requirementIndex` on the currently selected stage;
   * a no-op with nothing selected. */
  updateStageRequirement: (requirementIndex: number, updates: Partial<AspectRequirement>) => void;
  /** Removes the requirement at `requirementIndex` from the currently selected stage; a no-op
   * with nothing selected. */
  removeStageRequirement: (requirementIndex: number) => void;
  /**
   * Adds a new unwired manual stage after `afterStageId` (end of the list when `null`), always
   * marking the draft dirty (there is no no-op case — a new stage always changes the model).
   */
  addStage: (afterStageId: string | null, fallbackName: string, options?: { terminal?: boolean }) => void;
  /** Adds a new intent to `stageId`, attributed to `laneKey` (always marks the draft dirty). */
  addIntent: (stageId: string, laneKey: string, fallbackName: string) => void;
  /** Adds a new, unconfigured action to `stageId`'s onEnter list (always marks the draft dirty). */
  addAction: (stageId: string, actionType: ArcCanvasDroppableActionType) => void;
  /**
   * Adds a manual-exit target to `stageId` — the flow view's "Transition" button — pointing at
   * the first other stage `stageId` doesn't already target. No-op (does not mark dirty) when
   * `stageId` is unknown, the model has fewer than two stages, or every other stage is already
   * a target.
   */
  addTransitionTarget: (stageId: string) => void;
  /**
   * Replaces `model`'s stages with one of `ARC_CANVAS_STARTERS`'s shapes (the new-arc shape
   * picker's card choices) — always marks the draft dirty. A no-op with nothing loaded, or when
   * `starterId` doesn't match a known starter.
   */
  addStarterStages: (starterId: string) => void;
  /** `summary` region counts, derived from `model`; all zero while `model` is `null`. */
  summaryCounts: ArcCanvasSummaryCounts;
  /** Resets `model` to the loaded `definition`, clears `dirty` and every selection — a no-op
   * while nothing is loaded. Unlike a `definition` reference change, this does not refetch. */
  discard: () => void;
}

/**
 * @param definition - the loaded arc's definition (`ArcTemplateRow.definition`), or `null`
 *   while nothing is loaded. A new `definition` reference (a version switch, a reload, the row
 *   the server returned from a save) resets the model and `dirty`; the selection survives only
 *   when the stage/intent/action it names still exists in the new definition — so a save keeps
 *   the inspector open on what the author was editing, while a switch to an unrelated arc
 *   closes it.
 */
export function useArcEditorCanvasState(
  definition: ArcTemplateDefinition | null
): UseArcEditorCanvasStateResult {
  const initialModel = useMemo(
    () => (definition ? definitionToModel(definition) : null),
    [definition]
  );

  const [model, setModel] = useState<ArcCanvasModel | null>(initialModel);
  const [dirty, setDirty] = useState(false);
  const [unsavedChangeCount, setUnsavedChangeCount] = useState(0);
  // Counted from committed model identities rather than inside the `setModel` updaters, so a
  // StrictMode double-invoked updater cannot count one edit twice; landing back on the loaded
  // model (discard, or a save that swapped the baseline in) resets the count to zero.
  const lastCountedModel = useRef<ArcCanvasModel | null>(initialModel);
  useEffect(() => {
    if (model === lastCountedModel.current) return;
    lastCountedModel.current = model;
    if (model === initialModel) {
      setUnsavedChangeCount(0);
    } else {
      setUnsavedChangeCount((count) => count + 1);
    }
  }, [model, initialModel]);
  const [selectedArc, setSelectedArc] = useState(false);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<ArcCanvasTransitionSelection | null>(null);
  const [selectedIntent, setSelectedIntent] = useState<ArcCanvasIntentSelection | null>(null);
  const [selectedAction, setSelectedAction] = useState<ArcCanvasActionSelection | null>(null);

  useEffect(() => {
    setModel(initialModel);
    setDirty(false);
    const stageOf = (stageId: string) => initialModel?.stages.find((stage) => stage.id === stageId);
    setSelectedArc((prev) => prev && initialModel !== null);
    setSelectedStageId((prev) => (prev && stageOf(prev) ? prev : null));
    setSelectedTransition((prev) => {
      const stage = prev ? stageOf(prev.stageId) : undefined;
      if (!prev || !stage) return null;
      const actions = stage.transition?.availableActions ?? [];
      return prev.actionId === null || actions.some((action) => action.id === prev.actionId) ? prev : null;
    });
    setSelectedIntent((prev) =>
      prev && stageOf(prev.stageId)?.intents?.some((intent) => intent.id === prev.intentId) ? prev : null
    );
    setSelectedAction((prev) => {
      const stage = prev ? stageOf(prev.stageId) : undefined;
      const actions = [...(stage?.onEnter ?? []), ...(stage?.onExit ?? [])];
      return prev && actions.some((action) => action.id === prev.actionId) ? prev : null;
    });
  }, [initialModel]);

  const selectArc = useCallback(() => {
    setSelectedArc(true);
    setSelectedStageId(null);
    setSelectedTransition(null);
    setSelectedIntent(null);
    setSelectedAction(null);
  }, []);

  const updateArcMeta = useCallback((updates: ArcMetaUpdates) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = updateArcMetaInModel(prev, updates);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const updateArcSettings = useCallback((updates: ArcSettingsUpdates) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = updateArcSettingsInModel(prev, updates);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const addKeyDate = useCallback(() => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = addKeyDateToModel(prev);
      setDirty(true);
      return next;
    });
  }, []);

  const updateKeyDate = useCallback((name: string, updates: ArcKeyDateUpdates) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = updateKeyDateInModel(prev, name, updates);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const removeKeyDate = useCallback((name: string) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = removeKeyDateFromModel(prev, name);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const renameStage = useCallback((stageId: string, fallbackName: string) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = renameStageInModel(prev, stageId, fallbackName);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const selectStage = useCallback((stageId: string | null) => {
    setSelectedArc(false);
    setSelectedStageId(stageId);
    setSelectedTransition(null);
    setSelectedIntent(null);
    setSelectedAction(null);
  }, []);

  const selectIntent = useCallback((stageId: string, intentId: string) => {
    setSelectedArc(false);
    setSelectedStageId(null);
    setSelectedTransition(null);
    setSelectedIntent({ stageId, intentId });
    setSelectedAction(null);
  }, []);

  const selectAction = useCallback((stageId: string, actionId: string) => {
    setSelectedArc(false);
    setSelectedStageId(null);
    setSelectedTransition(null);
    setSelectedIntent(null);
    setSelectedAction({ stageId, actionId });
  }, []);

  const selectTransition = useCallback(
    (stageId: string, actionId: string | null = null) => {
      const stage = model?.stages.find((s) => s.id === stageId);
      setSelectedArc(false);
      setSelectedStageId(null);
      setSelectedTransition({ stageId, actionId: actionId ?? firstExitActionIdOf(stage?.transition) });
      setSelectedIntent(null);
      setSelectedAction(null);
    },
    [model]
  );

  const updateTransition = useCallback(
    (updates: ArcTransitionUpdates) => {
      if (!selectedTransition) return;
      setModel((prev) => {
        if (!prev) return prev;
        const next = updateTransitionInModel(prev, selectedTransition.stageId, selectedTransition.actionId, updates);
        if (next !== prev) setDirty(true);
        return next;
      });
    },
    [selectedTransition]
  );

  const removeTransition = useCallback(() => {
    if (!selectedTransition) return;
    setModel((prev) => {
      if (!prev) return prev;
      const next = removeTransitionFromModel(prev, selectedTransition.stageId, selectedTransition.actionId);
      if (next !== prev) setDirty(true);
      return next;
    });
    setSelectedTransition(null);
  }, [selectedTransition]);

  const updateIntent = useCallback(
    (updates: ArcIntentUpdates) => {
      if (!selectedIntent) return;
      setModel((prev) => {
        if (!prev) return prev;
        const next = updateIntentInModel(prev, selectedIntent.stageId, selectedIntent.intentId, updates);
        if (next !== prev) setDirty(true);
        return next;
      });
      if (updates.stageId && updates.stageId !== selectedIntent.stageId) {
        setSelectedIntent({ stageId: updates.stageId, intentId: selectedIntent.intentId });
      }
    },
    [selectedIntent]
  );

  const removeIntent = useCallback(() => {
    if (!selectedIntent) return;
    setModel((prev) => {
      if (!prev) return prev;
      const next = removeIntentFromModel(prev, selectedIntent.stageId, selectedIntent.intentId);
      if (next !== prev) setDirty(true);
      return next;
    });
    setSelectedIntent(null);
  }, [selectedIntent]);

  const updateAction = useCallback(
    (updates: ArcActionUpdates) => {
      if (!selectedAction) return;
      setModel((prev) => {
        if (!prev) return prev;
        const next = updateActionInModel(prev, selectedAction.stageId, selectedAction.actionId, updates);
        if (next !== prev) setDirty(true);
        return next;
      });
    },
    [selectedAction]
  );

  const removeAction = useCallback(() => {
    if (!selectedAction) return;
    setModel((prev) => {
      if (!prev) return prev;
      const next = removeActionFromModel(prev, selectedAction.stageId, selectedAction.actionId);
      if (next !== prev) setDirty(true);
      return next;
    });
    setSelectedAction(null);
  }, [selectedAction]);

  const updateStage = useCallback(
    (updates: ArcStageUpdates) => {
      if (!selectedStageId) return;
      setModel((prev) => {
        if (!prev) return prev;
        const next = updateStageInModel(prev, selectedStageId, updates);
        if (next !== prev) setDirty(true);
        return next;
      });
    },
    [selectedStageId]
  );

  const removeStage = useCallback(() => {
    if (!selectedStageId) return;
    setModel((prev) => {
      if (!prev) return prev;
      const next = removeStageFromModel(prev, selectedStageId);
      if (next !== prev) setDirty(true);
      return next;
    });
    setSelectedStageId(null);
  }, [selectedStageId]);

  const addStageRequirement = useCallback(() => {
    if (!selectedStageId) return;
    setModel((prev) => {
      if (!prev) return prev;
      const next = addStageRequirementToModel(prev, selectedStageId);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, [selectedStageId]);

  const updateStageRequirement = useCallback(
    (requirementIndex: number, updates: Partial<AspectRequirement>) => {
      if (!selectedStageId) return;
      setModel((prev) => {
        if (!prev) return prev;
        const next = updateStageRequirementInModel(prev, selectedStageId, requirementIndex, updates);
        if (next !== prev) setDirty(true);
        return next;
      });
    },
    [selectedStageId]
  );

  const removeStageRequirement = useCallback(
    (requirementIndex: number) => {
      if (!selectedStageId) return;
      setModel((prev) => {
        if (!prev) return prev;
        const next = removeStageRequirementFromModel(prev, selectedStageId, requirementIndex);
        if (next !== prev) setDirty(true);
        return next;
      });
    },
    [selectedStageId]
  );

  const addStage = useCallback((afterStageId: string | null, fallbackName: string, options?: { terminal?: boolean }) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = addStageToModel(prev, afterStageId, fallbackName, options);
      setDirty(true);
      return next;
    });
  }, []);

  const addIntent = useCallback((stageId: string, laneKey: string, fallbackName: string) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = addIntentToModel(prev, stageId, laneKey, fallbackName);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const addAction = useCallback((stageId: string, actionType: ArcCanvasDroppableActionType) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = addActionToModel(prev, stageId, actionType);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const addTransitionTarget = useCallback((stageId: string) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = addTransitionTargetToModel(prev, stageId);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const addStarterStages = useCallback((starterId: string) => {
    setModel((prev) => {
      if (!prev) return prev;
      const next = applyStarterToModel(prev, starterId);
      if (next !== prev) setDirty(true);
      return next;
    });
  }, []);

  const summaryCounts = useMemo(
    () => (model ? summaryCountsFor(model) : ZERO_SUMMARY_COUNTS),
    [model]
  );

  const discard = useCallback(() => {
    setModel(initialModel);
    setDirty(false);
    setSelectedArc(false);
    setSelectedStageId(null);
    setSelectedTransition(null);
    setSelectedIntent(null);
    setSelectedAction(null);
  }, [initialModel]);

  return {
    model,
    dirty,
    unsavedChangeCount,
    selectedArc,
    selectArc,
    updateArcMeta,
    updateArcSettings,
    addKeyDate,
    updateKeyDate,
    removeKeyDate,
    selectedStageId,
    selectStage,
    selectedTransition,
    selectTransition,
    updateTransition,
    removeTransition,
    selectedIntent,
    selectIntent,
    updateIntent,
    removeIntent,
    selectedAction,
    selectAction,
    updateAction,
    removeAction,
    renameStage,
    updateStage,
    removeStage,
    addStageRequirement,
    updateStageRequirement,
    removeStageRequirement,
    addStage,
    addIntent,
    addAction,
    addTransitionTarget,
    addStarterStages,
    summaryCounts,
    discard,
  };
}

export default useArcEditorCanvasState;
